import { CURRENCY_SYMBOL } from "../constants/currency";

/** Strip Sage prefix and fall back when not synced yet. */
export const formatSagePaymentRef = (
  sagePaymentNumber?: string | null,
  pendingLabel = "Pending Sage sync"
): string => {
  const sageRef = (sagePaymentNumber || "")
    .replace(/^\s*creation of\s+/i, "")
    .trim();
  return sageRef || pendingLabel;
};

export const pickSagePaymentNumber = (raw: Record<string, unknown>): string =>
  String(
    raw.sagePaymentNumber ??
      raw.SagePaymentNumber ??
      raw.sageX3Response ??
      raw.SageX3Response ??
      ""
  ).trim();

/** Merge payment metadata onto a row without changing PAY- invoice numbers. */
export const enrichInvoiceWithPayment = (
  row: {
    invoiceNumber?: string;
    orderNumber?: string;
    sagePaymentNumber?: string;
    paymentDate?: string;
    createdDate?: string;
    transactionID?: string;
    paymentMethod?: string;
  },
  payment: Record<string, unknown> | undefined
) => {
  if (!payment) return row;

  const sageRaw = pickSagePaymentNumber(payment);
  const paymentDate = pickPaymentDate(payment);
  const paymentType = String(payment.type ?? payment.Type ?? "");

  return {
    ...row,
    sagePaymentNumber: sageRaw,
    paymentDate: paymentDate ?? row.paymentDate,
    createdDate: paymentDate ?? row.createdDate,
    transactionID: String(
      payment.transactionId ?? payment.TransactionId ?? row.transactionID ?? ""
    ),
    paymentMethod:
      formatPaymentMethodLabel(paymentType, row.orderNumber) || row.paymentMethod,
  };
};

/** Prefer PaymentDate from API; fall back only when missing. */
export const pickPaymentDate = (raw: Record<string, unknown>): string | undefined => {
  const candidates = [raw.paymentDate, raw.PaymentDate];
  for (const value of candidates) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed || trimmed.startsWith("0001")) continue;
    return trimmed;
  }
  const fallback = raw.updatedAt ?? raw.UpdatedAt ?? raw.createdAt ?? raw.CreatedAt;
  return typeof fallback === "string" && fallback.trim() ? fallback.trim() : undefined;
};

export const formatPaymentMethodLabel = (
  type?: string | null,
  orderNumber?: string | null
): string => {
  const normalized = String(type ?? "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (normalized.includes("card")) return "Credit Card";
  if (normalized.includes("zain") || normalized === "wallet") {
    return "ZainCash / Mobile Wallet";
  }
  if (
    normalized.includes("cod") ||
    normalized.includes("cash") ||
    normalized.includes("delivery")
  ) {
    return "Cash on Delivery";
  }
  if (String(orderNumber ?? "").toUpperCase().startsWith("COD-")) {
    return "Cash on Delivery";
  }

  const raw = String(type ?? "").trim();
  return raw || "—";
};

export const formatIqdAmount = (amount: number | undefined | null): string =>
  `${CURRENCY_SYMBOL}${Number(amount ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Same amount-paid logic as the invoices table (exact decimals, no API rounding). */
export const resolveInvoiceAmountPaid = (invoice: {
  total?: number | null;
  remainingBalance?: number | null;
  amountPaid?: number | null;
  amountDue?: number | null;
}): number => {
  if (invoice.total != null && invoice.remainingBalance != null) {
    return Number(invoice.total) - Number(invoice.remainingBalance);
  }

  const candidates = [invoice.amountPaid, invoice.amountDue, invoice.total];
  for (const value of candidates) {
    const num = Number(value ?? 0);
    if (num > 0) return num;
  }
  return 0;
};

export const formatPaymentDisplayDate = (date?: string | null): string => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString("en-GB");
  } catch {
    return date;
  }
};

export const isCodOrderNumber = (value?: string | null): boolean =>
  /^COD-/i.test(String(value ?? "").trim());

/** "COD-7-2026-00013" / "SO-7-2026-00013" → "PAY-7-2026-00013" */
export const toPayNumber = (value?: string | null): string => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  const m = s.match(/^[A-Za-z]+[-/](.+)$/);
  return `PAY-${m ? m[1] : s}`;
};

/**
 * Display invoice number as PAY-… (matches mobile app).
 * Real Sage PAY- documents from the backend are shown as-is.
 */
export const formatDisplayInvoiceNumber = (
  invoiceNumber?: string | null,
  orderNumber?: string | null
): string => {
  const inv = String(invoiceNumber ?? "").trim();
  if (/^PAY-/i.test(inv)) return inv;

  const order = String(orderNumber ?? "").trim();
  // COD is paid per Sage invoice: show the real Sage invoice number, never PAY-<order>.
  if (isCodOrderNumber(order) && inv && inv.toLowerCase() !== order.toLowerCase()) {
    return inv;
  }
  if (order && order !== "Wallet Top-Up") {
    const fromOrder = toPayNumber(order);
    if (fromOrder) return fromOrder;
  }

  if (inv) return toPayNumber(inv);
  return "";
};
